import { useMemo, useState } from "react";
import { Plus, Search, MoreHorizontal, Pencil } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Field, FieldLabel } from "@/components/ui/field";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { LoadingAnimation } from "@/shared/components/LoadingAnimation";
import { useConfirm, withConfirmSuccess } from "@/shared/contexts/confirm-context";
import {
  useAllVehicles,
  useCreateVehicle,
  useUpdateVehicle,
} from "../hooks/useVehicles";
import type { VehicleDto } from "../services/vehiclesApi";

const VEHICLE_STATUS_OPTIONS = [
  "Available",
  "Unavailable",
  "Maintenance",
  "Breakdown",
  "Assigned",
  "Other",
] as const;

const CAPACITY_UNIT_OPTIONS = ["Trays"] as const;

const capacityLabel = (vehicle: VehicleDto) =>
  vehicle.capacity > 0 ? `${vehicle.capacity} ${vehicle.capacityUnit}` : "-";

const EMPTY_CREATE = {
  registrationNumber: "",
  nameOrDescription: "",
  vehicleType: "",
  capacity: "",
  capacityUnit: "Trays",
  status: "Available",
  notes: "",
};

const CreateVehicleDialog = () => {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_CREATE);
  const createMutation = useCreateVehicle();
  const confirm = useConfirm();

  const set = (field: keyof typeof EMPTY_CREATE, value: string) =>
    setForm((prev) => ({ ...prev, [field]: value }));

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const capacity = form.capacity.trim() === "" ? undefined : Number(form.capacity);
    if (capacity !== undefined && (!Number.isFinite(capacity) || capacity < 0)) return;
    const capacityUnit = capacity !== undefined && capacity > 0 ? form.capacityUnit : undefined;

    confirm({
      title: "Create vehicle?",
      description: `${form.registrationNumber.trim().toUpperCase()} will be added to the dispatch fleet.`,
      confirmLabel: "Create vehicle",
      onConfirm: () =>
        withConfirmSuccess(
          createMutation.mutateAsync({
            registrationNumber: form.registrationNumber.trim().toUpperCase(),
            nameOrDescription: form.nameOrDescription.trim(),
            vehicleType: form.vehicleType.trim() || undefined,
            capacity,
            capacityUnit,
            status: form.status,
            notes: form.notes.trim() || undefined,
          }),
          "Vehicle created"
        ).then(() => {
          setForm(EMPTY_CREATE);
          setOpen(false);
        }),
    });
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button>
          <Plus className="w-4 h-4 mr-2" />
          Add Vehicle
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle>Register New Vehicle</DialogTitle>
          <DialogDescription>
            Add a dispatch vehicle for delivery planning and assignment.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-5">
          <div className="grid gap-4 md:grid-cols-2">
            <Field>
              <FieldLabel>Registration Number *</FieldLabel>
              <Input
                placeholder="e.g. KDA 123A"
                value={form.registrationNumber}
                onChange={(e) => set("registrationNumber", e.target.value)}
                required
              />
            </Field>
            <Field>
              <FieldLabel>Name / Description *</FieldLabel>
              <Input
                placeholder="e.g. Isuzu 5T Truck"
                value={form.nameOrDescription}
                onChange={(e) => set("nameOrDescription", e.target.value)}
                required
              />
            </Field>
            <Field>
              <FieldLabel>Vehicle Type</FieldLabel>
              <Input
                placeholder="e.g. Truck"
                value={form.vehicleType}
                onChange={(e) => set("vehicleType", e.target.value)}
              />
            </Field>
            <Field>
              <FieldLabel>Status *</FieldLabel>
              <Select value={form.status} onValueChange={(v) => set("status", v)}>
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {VEHICLE_STATUS_OPTIONS.map((status) => (
                    <SelectItem key={status} value={status}>{status}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field>
              <FieldLabel>Capacity</FieldLabel>
              <Input
                type="number"
                min="0"
                step="0.01"
                placeholder="Optional"
                value={form.capacity}
                onChange={(e) => set("capacity", e.target.value)}
              />
            </Field>
            <Field>
              <FieldLabel>Capacity Unit</FieldLabel>
              <Select value={form.capacityUnit} onValueChange={(v) => set("capacityUnit", v)}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Optional" />
                </SelectTrigger>
                <SelectContent>
                  {CAPACITY_UNIT_OPTIONS.map((unit) => (
                    <SelectItem key={unit} value={unit}>{unit}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
          </div>
          <Field>
            <FieldLabel>Notes</FieldLabel>
            <Textarea
              placeholder="Optional notes"
              value={form.notes}
              onChange={(e) => set("notes", e.target.value)}
              rows={3}
            />
          </Field>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={createMutation.isPending}>
              {createMutation.isPending ? "Saving..." : "Create Vehicle"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const EditVehicleDialog = ({
  vehicle,
  open,
  onOpenChange,
}: {
  vehicle: VehicleDto;
  open: boolean;
  onOpenChange: (value: boolean) => void;
}) => {
  const [form, setForm] = useState({
    registrationNumber: vehicle.registrationNumber,
    nameOrDescription: vehicle.nameOrDescription,
    vehicleType: vehicle.vehicleType ?? "",
    capacity: vehicle.capacity > 0 ? String(vehicle.capacity) : "",
    capacityUnit: "Trays",
    status: vehicle.status,
    notes: vehicle.notes ?? "",
    isActive: vehicle.isActive,
  });

  const updateMutation = useUpdateVehicle();
  const confirm = useConfirm();

  const set = <K extends keyof typeof form>(field: K, value: (typeof form)[K]) =>
    setForm((prev) => ({ ...prev, [field]: value }));

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const capacity = form.capacity.trim() === "" ? undefined : Number(form.capacity);
    if (capacity !== undefined && (!Number.isFinite(capacity) || capacity < 0)) return;
    const capacityUnit = capacity !== undefined && capacity > 0 ? form.capacityUnit : undefined;

    confirm({
      title: "Save vehicle changes?",
      description: `Changes to ${form.registrationNumber.trim().toUpperCase()} will be saved.`,
      confirmLabel: "Save changes",
      onConfirm: () =>
        withConfirmSuccess(
          updateMutation.mutateAsync({
            id: vehicle.id,
            data: {
              registrationNumber: form.registrationNumber.trim().toUpperCase(),
              nameOrDescription: form.nameOrDescription.trim(),
              vehicleType: form.vehicleType.trim() || undefined,
              capacity,
              capacityUnit,
              status: form.status,
              notes: form.notes.trim() || undefined,
              isActive: form.isActive,
            },
          }),
          "Vehicle updated"
        ).then(() => onOpenChange(false)),
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle>Edit Vehicle</DialogTitle>
          <DialogDescription>
            Update dispatch readiness, identity details, and carrying capacity.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-5">
          <div className="grid gap-4 md:grid-cols-2">
            <Field>
              <FieldLabel>Registration Number *</FieldLabel>
              <Input
                value={form.registrationNumber}
                onChange={(e) => set("registrationNumber", e.target.value)}
                required
              />
            </Field>
            <Field>
              <FieldLabel>Name / Description *</FieldLabel>
              <Input
                value={form.nameOrDescription}
                onChange={(e) => set("nameOrDescription", e.target.value)}
                required
              />
            </Field>
            <Field>
              <FieldLabel>Vehicle Type</FieldLabel>
              <Input
                value={form.vehicleType}
                onChange={(e) => set("vehicleType", e.target.value)}
              />
            </Field>
            <Field>
              <FieldLabel>Status *</FieldLabel>
              <Select value={form.status} onValueChange={(v) => set("status", v)}>
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {VEHICLE_STATUS_OPTIONS.map((status) => (
                    <SelectItem key={status} value={status}>{status}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field>
              <FieldLabel>Capacity</FieldLabel>
              <Input
                type="number"
                min="0"
                step="0.01"
                placeholder="Optional"
                value={form.capacity}
                onChange={(e) => set("capacity", e.target.value)}
              />
            </Field>
            <Field>
              <FieldLabel>Capacity Unit</FieldLabel>
              <Select value={form.capacityUnit} onValueChange={(v) => set("capacityUnit", v)}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Optional" />
                </SelectTrigger>
                <SelectContent>
                  {CAPACITY_UNIT_OPTIONS.map((unit) => (
                    <SelectItem key={unit} value={unit}>{unit}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
          </div>
          <Field>
            <FieldLabel>Notes</FieldLabel>
            <Textarea
              value={form.notes}
              onChange={(e) => set("notes", e.target.value)}
              rows={3}
            />
          </Field>
          <div className="flex items-center gap-3">
            <Checkbox
              id="vehicle-is-active"
              checked={form.isActive}
              onCheckedChange={(v) => set("isActive", v === true)}
            />
            <Label htmlFor="vehicle-is-active">Active</Label>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={updateMutation.isPending}>
              {updateMutation.isPending ? "Saving..." : "Save Changes"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const VehiclesTable = ({ vehicles }: { vehicles: VehicleDto[] }) => {
  const [search, setSearch] = useState("");
  const [editTarget, setEditTarget] = useState<VehicleDto | null>(null);

  const filtered = useMemo(
    () =>
      vehicles.filter(
        (vehicle) =>
          vehicle.registrationNumber.toLowerCase().includes(search.toLowerCase()) ||
          vehicle.nameOrDescription.toLowerCase().includes(search.toLowerCase()) ||
          (vehicle.vehicleType ?? "").toLowerCase().includes(search.toLowerCase())
      ),
    [vehicles, search]
  );

  return (
    <div className="space-y-4">
      <div className="relative w-72 ml-auto">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input
          className="pl-9"
          placeholder="Search vehicles..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      <div className="rounded-lg border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Registration</TableHead>
              <TableHead>Description</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Capacity</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Active</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} className="text-center text-muted-foreground py-8">
                  No vehicles found.
                </TableCell>
              </TableRow>
            )}
            {filtered.map((vehicle) => (
              <TableRow key={vehicle.id}>
                <TableCell className="font-medium">{vehicle.registrationNumber}</TableCell>
                <TableCell>{vehicle.nameOrDescription}</TableCell>
                <TableCell>{vehicle.vehicleType ?? "-"}</TableCell>
                <TableCell>{capacityLabel(vehicle)}</TableCell>
                <TableCell>
                  <Badge variant="outline">{vehicle.status}</Badge>
                </TableCell>
                <TableCell>
                  <Badge variant={vehicle.isActive ? "default" : "secondary"}>
                    {vehicle.isActive ? "Active" : "Inactive"}
                  </Badge>
                </TableCell>
                <TableCell className="text-right">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon">
                        <MoreHorizontal className="w-4 h-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => setEditTarget(vehicle)}>
                        <Pencil className="w-4 h-4 mr-2" />
                        Edit
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {editTarget && (
        <EditVehicleDialog
          vehicle={editTarget}
          open={!!editTarget}
          onOpenChange={(value) => {
            if (!value) setEditTarget(null);
          }}
        />
      )}
    </div>
  );
};

export const VehiclesManagementPage = () => {
  const { data: vehicles = [], isLoading, error } = useAllVehicles();

  const orderedVehicles = useMemo(
    () =>
      [...vehicles].sort((a, b) =>
        a.registrationNumber.localeCompare(b.registrationNumber)
      ),
    [vehicles]
  );

  return (
    <main className="space-y-6 p-6">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">Vehicles</h1>
          <p className="text-sm text-muted-foreground">
            Manage delivery vehicles for dispatch planning and assignment.
          </p>
        </div>
        <CreateVehicleDialog />
      </div>

      {isLoading ? (
        <LoadingAnimation />
      ) : error ? (
        <div className="rounded-lg border border-destructive/30 bg-destructive/10 p-4 text-sm text-destructive">
          Failed to load vehicles.
        </div>
      ) : (
        <VehiclesTable vehicles={orderedVehicles} />
      )}
    </main>
  );
};
